import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import { red } from '@mui/material/colors';

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual, size, replace, isNull, isUndefined } from "lodash";
import { format } from "date-fns";

import rpOrderListService from "@plannel/services/rp/rp-order-list-service"
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import { FilterButton } from "@plannel/components/ActionIconButton";
import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import AttributeConfigModal from "@plannel/components/modal/AttributeConfigModal";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import CustomHeaderGroup from "@plannel/components/aggrid/CustomHeaderGroup";
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import NumberUtil from "@plannel/utils/number-util";

import FilterContainer from "@plannel/components/layout/FilterContainer";
import DateCondition from "@plannel/components/DateCondition";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomTooltip from "@plannel/components/aggrid/CustomTooltip";

const { snackBarMessageColor } = ColorUtil;
const { versionStatusCode, orderListCondition } = CodeUtil;
const { SIMULATION, PLANNED, RELEASED } = versionStatusCode;
const RpOrderList = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);
  const resultTotalCount = useRef(null);

  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { originLocation: null, destLocation: null, category: null });
  const [itemHrchyCustomValue, setItemHrchyCustomValue] = useState(null);
  const initPeriod = { start: reduxViewState?.period?.start ? new ZDate(reduxViewState.period.start) : null, end: reduxViewState?.period?.end ? new ZDate(reduxViewState.period.end) : null };
  const [period, setPeriod] = useState(initPeriod);
  const [selectedSearch, setSelectedSearch] = useState(reduxViewState?.selectedSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ? reduxViewState.itemSearch : "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters?.children?.length > 0 ? reduxViewState.advancedFilters : null);
  const [pendingAdvancedFilters, setPendingAdvancedFilters] = useState(null);
  const [isAFActive, setIsAFActive] = useState(!!(reduxViewState?.advancedFilters?.children?.length > 0));
  const [openFilter, setOpenFilter] = useState(false);

  const [sortParams, setSortParams] = useState({});
  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [attrColDefs, setAttrColDefs] = useState([]);
  const attrFields = ["attr01", "attr02", "attr03", "attr04", "attr05"];
  const EXCEL_EXPORT = "excelExport";

  const [rpOrderList, setRpOrderList] = useState([]);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 확정 결과화면 진입 시 redux에는 { id, name } 부분 객체만 주입되고, planConfigObj는
     RpVersionFilter 가 버전 목록을 조회한 뒤 비동기로 되채운다. 그 전까지 bucketUnit 은 undefined. */
  const bucketUnit = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
  const versionReady = !isEmpty(rpVersionFilter) && !isUndefined(bucketUnit);
  const isMonthBucket = isEqual(bucketUnit, CodeUtil.buckets.MONTH);

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.destLocCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }

    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const dataCellStyle = (params) => {
    const cellStyle = {};

    if (params.value < 0) {
       cellStyle.color = red[500];
    }

    return cellStyle;
  }

  const prevVersionName = useMemo(
    () => rpOrderList?.[0]?.prevReleaseVersionName ?? ''
     // eslint-disable-next-line react-hooks/exhaustive-deps
    , [rpOrderList]);

  const columnDefs = useMemo(() => [
    {
      headerName: "originLocation",
      children: [
        { headerName: "locCd", field: "originLocCd", width: 120, type: ["nonEditableColumn"], cellClass: 'stringType' },
        { headerName: "locName", field: "originLocName", width: 120, type: ["nonEditableColumn"] },
        { headerName: "locDesc", field: "originLocDesc", width: 120, type: ["nonEditableColumn"], hide: true }
      ]
    },
    {
      headerName: "destLocation",
      children: [
        { headerName: "locCd", field: "destLocCd", width: 120, type: ["nonEditableColumn"], cellClass: 'stringType' },
        { headerName: "locName", field: "destLocName", width: 120, type: ["nonEditableColumn"] },
        { headerName: "locDesc", field: "destLocDesc", width: 120, type: ["nonEditableColumn"], hide: true }
      ]
    },
    { headerName: "orderDate", field: "displayOrderDate", width: 120, type: ["nonEditableColumn"] },
    { headerName: "dueIn", field: "displayDueIn", width: 120, type: ["nonEditableColumn"], sortable: false },
    {
      headerName: "item", groupId: "itemGrp", headerGroupComponent: CustomHeaderGroup,
      children: [
        { headerName: "itemCd", field: "itemCd", minWidth: 120, width: 150, type: ["nonEditableColumn"], cellClass: 'stringType' },
        { headerName: "itemName", field: "itemName", minWidth: 180, width: 150, type: ["nonEditableColumn"] },
        { headerName: "itemHrchy", field: "itemHrchy", minWidth: 180, width: 150, type: ["nonEditableColumn"] },
        ...attrColDefs,
      ]
    },
    { headerName: "replenishOrderQty", field: "replenishQty", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
    { headerName: "replenishOrderAmt", field: "replenishAmt", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, sortable: false },
    { headerName: "prevReplenishOrderQty", headerTooltip: `${t("prevVersionName", { name: prevVersionName })}`, tooltipComponent: CustomTooltip, field: "prevReplenishQty",
      width: 180, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
    { 
      headerName: "prevReplenishOrderAmt", headerTooltip: `${t("prevVersionName", { name: prevVersionName })}`, tooltipComponent: CustomTooltip,
      field: "prevReplenishAmt", width: 180, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, sortable: false
    },
    { 
      headerName: "replenishOrderQtyGap", headerTooltip: `${t("prevVersionName", { name: prevVersionName })}`, tooltipComponent: CustomTooltip, field: "replenishQtyGap",
      width: 180, type: ["numberColumn", "nonEditableColumn", "rightAligned"], cellStyle : params => dataCellStyle(params), sortable: false
    },
    { headerName: "replenishOrderAmtGap", headerTooltip: `${t("prevVersionName", { name: prevVersionName })}`, tooltipComponent: CustomTooltip, sortable: false,
      field: "replenishAmtGap", width: 180, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, cellStyle : params => dataCellStyle(params)
    },
    { headerName: "invUnitCost", field: "invUnitCost", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, sortable: false },
    { headerName: "purchaseOrderQty", field: "purchaseQty", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
    { headerName: "purchaseOrderAmt", field: "purchaseAmt", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, sortable: false },
    { headerName: "prevPurchaseOrderQty", headerTooltip: `${t("prevVersionName", { name: prevVersionName })}`, tooltipComponent: CustomTooltip, field: "prevPurchaseQty",
      width: 180, type: ["numberColumn", "nonEditableColumn", "rightAligned"]
    },
    { headerName: "prevPurchaseOrderAmt", headerTooltip: `${t("prevVersionName", { name: prevVersionName })}`, tooltipComponent: CustomTooltip, field: "prevPurchaseAmt",
      width: 180, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, sortable: false
    },
    { headerName: "purchaseOrderQtyGap", headerTooltip: `${t("prevVersionName", { name: prevVersionName })}`, tooltipComponent: CustomTooltip, field: "purchaseQtyGap",
      width: 180, type: ["numberColumn", "nonEditableColumn", "rightAligned"], sortable: false, cellStyle : params => dataCellStyle(params)
    },
    { headerName: "purchaseOrderAmtGap", headerTooltip: `${t("prevVersionName", { name: prevVersionName })}`, tooltipComponent: CustomTooltip, field: "purchaseAmtGap",
      width: 180, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, sortable: false, cellStyle : params => dataCellStyle(params)
    },
    { headerName: "purchaseUnitPrice", field: "purchaseUnitPrice", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, sortable: false },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [prevVersionName, attrColDefs]);

  const dateFilterUnit = isMonthBucket ? "month" : null;
  const advancedFilterColumnDefs = [
    {
      headerName: "originLocation",
      children: [
        { headerName: "locCd", filterType: "string", filterName: "originLocCd" },
        { headerName: "locName", filterType: "string", filterName: "originLocName" },
      ]
    },
    {
      headerName: "destLocation",
      children: [
        { headerName: "locCd", filterType: "string", filterName: "destLocCd" },
        { headerName: "locName", filterType: "string", filterName: "destLocName" },
      ]
    },
    { headerName: "orderDate", filterType: "date", filterName: "orderDate", filterUnit: dateFilterUnit },
    { headerName: "dueIn", filterType: "date", filterName: "dueIn", filterUnit: dateFilterUnit },
    {
      headerName: "item",
      children: [
        { headerName: "itemCd", filterType: "string", filterName: "itemCd" },
        { headerName: "itemName", filterType: "string", filterName: "itemName" },
        { headerName: "itemHrchy", filterType: "string", filterName: "itemHrchy" },
        ...attrColDefs.map(colDef => ({ headerName: colDef.headerName, filterType: "string", filterName: colDef.field })),
      ]
    },
    { headerName: "replenishOrderQty", filterType: "number", filterName: "replenishQty" },
    { headerName: "prevReplenishOrderQty", filterType: "number", filterName: "prevReplenishQty" },
    { headerName: "replenishOrderQtyGap", filterType: "number", filterName: "replenishQtyGap" },
    { headerName: "purchaseOrderQty", filterType: "number", filterName: "purchaseQty" },
    { headerName: "prevPurchaseOrderQty", filterType: "number", filterName: "prevPurchaseQty" },
    { headerName: "purchaseOrderQtyGap", filterType: "number", filterName: "purchaseQtyGap" },
  ];

  const toFilterDate = date => {
    try {
      return format(date, isMonthBucket ? "yyyy-MM-01" : "yyyy-MM-dd");
    } catch {
      return null;
    }
  };

  const buildInitialFromCurrentFilters = () => {
    const nodes = [];

    const pushEquals = (filterName, values) => {
      const targets = (values ?? []).filter(Boolean);
      if (targets.length > 1) {
        nodes.push({ logicOperator: "OR", children: targets.map(value => ({ filterType: "string", headerName: filterName, filterName, operator: "equals", values: [value] })) });
      } else if (targets.length === 1) {
        nodes.push({ filterType: "string", headerName: filterName, filterName, operator: "equals", values: [targets[0]] });
      }
    };

    pushEquals("originLocCd", filterValue?.originLocation?.map(location => location?.locCd));
    pushEquals("destLocCd", filterValue?.destLocation?.map(location => location?.locCd));
    pushEquals("itemHrchy", filterValue?.category?.filter(category => category?.leafFlg).map(category => category?.name));
    pushEquals("itemCd", [itemSearch.current, ...(extraItemCodesRef.current ?? [])]);

    if (selectedSearch?.cd && (period?.start || period?.end)) {
      const startValue = period?.start ? toFilterDate(period.start) : null;
      const endValue = period?.end ? toFilterDate(period.end) : null;
      const dateNode = { filterType: "date", headerName: selectedSearch.cd, filterName: selectedSearch.cd, filterUnit: dateFilterUnit };

      if (startValue && endValue) {
        nodes.push({ ...dateNode, operator: "inRange", values: [startValue, endValue] });
      } else if (startValue) {
        nodes.push({ ...dateNode, operator: "greaterThanOrEqual", values: [startValue] });
      } else if (endValue) {
        nodes.push({ ...dateNode, operator: "lessThanOrEqual", values: [endValue] });
      }
    }

    return nodes.length > 0 ? { logicOperator: "AND", children: [{ logicOperator: "AND", children: nodes }] } : { logicOperator: "AND", children: [] };
  };

  const handleFilterOpen = () => {
    if (!openFilter) {
      setPendingAdvancedFilters(isAFActive ? null : buildInitialFromCurrentFilters());
    }
    setOpenFilter(prev => !prev);
  };

  const handleAdvancedFilterApply = (filters) => {
    const normalized = filters?.children?.length > 0 ? filters : null;
    const clearedFilterValue = { originLocation: null, destLocation: null, category: null };

    setFilterValue(clearedFilterValue);
    setItemHrchyCustomValue([]);
    setSelectedSearch(null);
    setPeriod({ start: null, end: null });
    itemSearch.current = "";
    extraItemCodesRef.current = [];
    setAdvancedFilters(normalized);
    setPendingAdvancedFilters(normalized);
    setIsAFActive(!!normalized);
    resultTotalCount.current = null;
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createFilterViewState(viewName, clearedFilterValue)));
  };

  const resetAdvancedFilter = () => {
    if (isAFActive) {
      setIsAFActive(false);
    }
    setPendingAdvancedFilters({ logicOperator: "AND", children: [] });
    setAdvancedFilters(null);
    setOpenFilter(false);
  };

  const handleFilterValueChange = (newValue) => {
    if (isAFActive) {
      if (!(newValue?.originLocation?.length > 0 || newValue?.destLocation?.length > 0 || newValue?.category?.length > 0)) {
        return;
      }
      resetAdvancedFilter();
    }
    setFilterValue(newValue);
  };

  const handleSelectedSearchChange = (newValue) => {
    if (isAFActive) {
      resetAdvancedFilter();
    }
    setSelectedSearch(newValue);
  };

  const handlePeriodChange = (newValue) => {
    if (isAFActive) {
      resetAdvancedFilter();
    }
    setPeriod(newValue);
  };

  const handleItemSearchRetrieve = () => {
    if (isAFActive) {
      resetAdvancedFilter();
    }
    initPageToRetrieve();
  };

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (versionReady) {
      searchFilters.versionId = rpVersionFilter.id;
      searchFilters.bucket = bucketUnit;
    }

    if (!isAFActive) {
      if (filterValue?.originLocation) {
        searchFilters.originLocCd = filterValue?.originLocation?.map(location => location?.locCd);
      }

      if (filterValue?.destLocation) {
        searchFilters.destLocCd = filterValue?.destLocation?.map(location => location?.locCd);
      }

      if (filterValue?.category) {
        searchFilters.itemHrchyCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
      }

      if (selectedSearch) {
        searchFilters.condition = selectedSearch.cd;
      }

      if (period?.start) {
        searchFilters.startDate = period.start;
      }

      if (period?.end) {
        searchFilters.endDate = period.end;
      }

      if (extraItemCodesRef.current?.length > 0) {
        searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
      } else if (itemSearch.current) {
        searchFilters.item = itemSearch.current;
      }
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    searchFilters.totalCount = resultTotalCount.current ?? 0;
    params.pagination = isPagination;
    params.searchFilters = searchFilters;

    if (isAFActive && advancedFilters?.children?.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    return { ...params, ...sortParams };
  }

  const retrieveOrderList = () => {
    gridRef.current?.api?.setGridOption('loading', true);

    const params = getRequestParams();
    rpOrderListService.getAll(params)
      .then(response => {
        const { results, totalPages, totalItems, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setRpOrderList(results);
        resultTotalCount.current = totalItems;
        isEmpty(results) ? setGridPages(0) : setGridPages(totalPages);
        if (size(currency)) {
          currencyMap.current = currency;
        }
      })
      .catch(e => {
        const { message } = e.response?.data;
        if (message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t(message) });
        }
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, period, selectedSearch, currentPage, pageSize, advancedFilters: isAFActive ? advancedFilters : null });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const onGridReady = () => {
    setGridLoading(true);
  }

  const retrieveExportColumns = () => {
    const exportColumns = columnDefs.map(col => {
      if (col.children) {
        const visibleChildren = col.children.filter(child => !child.hide);
        return { ...col, children: visibleChildren };
      }
      return col;
    });
    return exportColumns
  };

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveOrderList();
    }
  }

  useEffect(() => {
    if (gridLoading && versionReady) {
      retrieveOrderList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  const dateFilterKey = selectedSearch && (period?.start || period?.end)
    ? [selectedSearch.cd, period?.start?.getTime() ?? "", period?.end?.getTime() ?? ""].join("|")
    : "";

  /* bucketUnit 을 dep 에 포함해야 부분 객체(id 동일) → 완전 객체 재수화 시 조회가 다시 돈다. */
  useEffect(() => {
    if (gridLoading && versionReady) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter?.id, bucketUnit, filterValue.originLocation, filterValue.destLocation, filterValue.category, dateFilterKey, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(attrColDefs)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attrColDefs, columnDefs]);


  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED, RELEASED]} />
        <LocationTreeFilter viewName={viewName} title={"originLocation"} filterValue={filterValue?.originLocation?.map(location => location?.locCd)} onChangeFilter={handleFilterValueChange} />
        <LocationTreeFilter viewName={viewName} title={"destLocation"} filterValue={filterValue?.destLocation?.map(location => location?.locCd)} onChangeFilter={handleFilterValueChange} />
        <DateCondition
          conditionList={orderListCondition}
          period={period}
          useAutoComplete
          views={isMonthBucket ? ['year', 'month'] : ['year', 'month', 'day']}
          dateFormat={isMonthBucket ? 'yyyy-MM' : 'yyyy-MM-dd'}
          openTo={isMonthBucket ? 'month' : 'day'}
          selectedSearch={selectedSearch}
          setSelectedSearch={handleSelectedSearchChange}
          setPeriod={handlePeriodChange}
        />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={handleFilterValueChange} customValue={itemHrchyCustomValue} enableMultiSelect selectLeafs/>
        <RPItemAutocomplete retrieve={handleItemSearchRetrieve} searchValue={itemSearch} sx={{ mr: 1 }} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
          <ExcelExportButton title={title} service={rpOrderListService} columnDefs={retrieveExportColumns()} params={getRequestParams(false)} />
          <FilterButton label="advancedFilter" advancedFilterColor={isAFActive ? "info" : "default"} onClick={handleFilterOpen} />
        </Box>
      </FilterContainer>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box id="rp-order-list" className="ag-theme-balham grid-area" mb={1}>
            <AgGridReact
              ref={gridRef}
              rowData={rpOrderList}
              statusBar={statusBar}
              {...defaultGridMemo}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          <PaginationContainer
            currentPage={currentPage} totalPages={gridPages}
            pageSize={pageSize} pageSizes={pageSizes}
            setCurrentPage={setCurrentPage} setPageSize={setPageSize}
          />
        </Box>
        {openFilter && (
          <>
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={advancedFilterColumnDefs}
                advancedFilters={pendingAdvancedFilters ?? advancedFilters}
                onApply={handleAdvancedFilterApply}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          </>
        )}
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <AttributeConfigModal
        open={false}
        target={"item"}
        attrFields={attrFields}
        setAttrColDefs={setAttrColDefs}
        attrColumnGroupOptions={{ columnGroupShow: "open", sortable: false }}
      />
    </>
  );
};

export default withTranslation()(RpOrderList);